// src/app/(dashboard)/meter/components/RefreshStatusBadge.tsx
// Shared across /meter (list) and /meter/[peaNo] (detail).
"use client";

import React from "react";

interface RefreshStatusBadgeProps {
  secondsUntilRefresh: number | null;
  isRefreshing: boolean;
  isOnline: boolean;
  onManualRefresh: () => void;
}

function fmtCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function RefreshStatusBadge({
  secondsUntilRefresh,
  isRefreshing,
  isOnline,
  onManualRefresh,
}: RefreshStatusBadgeProps) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
      {/* Offline warning — takes priority over everything */}
      {!isOnline && (
        <span
          className={[
            "inline-flex items-center gap-1 rounded-lg px-2 py-1",
            "border border-amber-300 bg-amber-50 text-amber-700",
            "dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300",
          ].join(" ")}
        >
          <OfflineIcon />
          ออฟไลน์
        </span>
      )}

      {/* Countdown or refreshing spinner — only when online */}
      {isOnline && (
        <span
          className={[
            "inline-flex min-w-[6.5rem] items-center justify-center gap-1 rounded-lg px-2 py-1 font-mono",
            "border border-slate-200 dark:border-slate-700",
            isRefreshing
              ? "text-blue-600 dark:text-blue-400"
              : "text-slate-500 dark:text-slate-400",
          ].join(" ")}
        >
          {isRefreshing ? (
            <>
              <Spinner />
              กำลังรีเฟรช…
            </>
          ) : secondsUntilRefresh !== null ? (
            <>
              <ClockIcon />
              {fmtCountdown(secondsUntilRefresh)}
            </>
          ) : null}
        </span>
      )}

      {/* Manual refresh button */}
      <button
        onClick={onManualRefresh}
        disabled={isRefreshing || !isOnline}
        title="รีเฟรชตอนนี้"
        className={[
          "rounded-lg border p-1.5 transition-colors",
          "border-slate-200 dark:border-slate-700",
          "hover:bg-slate-100 dark:hover:bg-slate-800",
          "disabled:cursor-not-allowed disabled:opacity-50",
        ].join(" ")}
      >
        <RefreshIcon className={isRefreshing ? "animate-spin" : ""} />
      </button>
    </div>
  );
}

function RefreshIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function OfflineIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0" />
      <path d="M2 8.82a15 15 0 0 1 4.17-2.65" />
      <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.76" />
      <path d="M16.85 11.25a10 10 0 0 1 2.22 1.68" />
      <path d="M5 12.85a10 10 0 0 1 5.26-2.79" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
